import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApiHook from "../hooks/useApiHook";
import { SocialLogin } from "@capgo/capacitor-social-login";
import config from "../config";
import { Preferences } from "@capacitor/preferences";
import useAppView from "../components/platforms/useAppView";

export const isPsychologistProfileComplete = (user) => {
    if (!user) {
        return false;
    }

    return Boolean(
        String(user.titolo || '').trim() &&
        String(user.professione || '').trim() &&
        String(user.orientamentoPrimario || user.orientamentoPsicologico || '').trim() &&
        String(user.prefissoTelefono || '').trim() &&
        String(user.numeroTelefono || '').trim()
    );
};

const AuthContext = createContext({
    user: null,
    response: null,
    token : null,
    isLoading: false,
    isAuthenticated: false,
    isProfileComplete: false,
    googleAuth: null,
    googleMobileLogin: () => { },
    completeAuthentication: async () => null,
    requestAdminCode: async () => null,
    verifyAdminCode: async () => null
})

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [response, setResponse] = useState(null);
    const [token, setToken] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isProfileComplete, setIsProfileComplete] = useState(false);
    const navigate = useNavigate();
    const { response: responseSuccess, error: responseError, makeRequest } = useApiHook();
    const { makeRequest: makeAdminRequest } = useApiHook();
    const { isNativePlatform } = useAppView();

    useEffect(() => {
        if (isNativePlatform && config.HAS_GOOGLE_OAUTH_CLIENT_ID) {
            SocialLogin.initialize({
                google: {
                    webClientId: config.GOOGLE_OAUTH_CLIENT_ID,
                },
            });
        }
    }, [isNativePlatform]);

    const updateIsAuthenticated = (value) => {
        setIsAuthenticated(value);
        localStorage.setItem('isAuthenticated', value.toString());
    };

    const completeAuthentication = async (authData, navigateTo = "/") => {
        if (!authData?.token || !authData?.user) {
            return false;
        }

        const nextIsProfileComplete = isPsychologistProfileComplete(authData.user);
        await storeSession(authData.token);
        setUser(authData.user);
        updateIsAuthenticated(true);
        setIsProfileComplete(nextIsProfileComplete);
        if (navigateTo) {
            navigate(nextIsProfileComplete ? navigateTo : "/scheda-psicologo");
        }
        return true;
    };

    const storeSession = async (newToken) => {
        setToken(newToken);
        if (isNativePlatform) {
            await Preferences.set({key: 'token', value: newToken});
            return;
        }
        localStorage.setItem('token', newToken);
    };

    const deleteSession = async () => {
        setToken(null);
        if (isNativePlatform) {
            await Preferences.remove({key: 'token'});
            return;
        }
        localStorage.removeItem('token');
    };

    const readSession = async () => {
        let sessionToken;
        if (isNativePlatform) {
            const { value } = await Preferences.get({key: 'token'});
            sessionToken = value;
        } else {
            sessionToken = localStorage.getItem('token');
        }
        setToken(sessionToken);
        return sessionToken;
    };

    useEffect(() => {
        const tokenCheck = async () => {
            const authToken = await readSession();
            if (!authToken) {
                setIsLoading(false);
                return;
            }
            makeRequest('/auth/autologin', 'post', { requestType: 'autologin' }, { token: authToken });
        }
        tokenCheck();
    }, []);

    useEffect(() => {
        if (!responseError) return;
        setResponse(responseError);
        setIsLoading(false);
        (async () => {
            const statusCode = responseError?.response?.status || responseError?.status;
            if (statusCode === 401 || statusCode === 403) {
                console.log(`[AUTH] Errore ${statusCode} rilevato - logout automatico`);
                setUser(null);
                updateIsAuthenticated(false);
                setIsProfileComplete(false);
                await deleteSession();
                navigate("/auth");
            }
        })();

    }, [responseError]);

    useEffect(() => {
        const res = responseSuccess;
        if (!res || !res.request || !res.request.requestType) return;

        setIsLoading(false);

        switch (res.request.requestType) {
            case 'register':
                setResponse(res);
                break;
            case 'login':
            case 'google':
                setResponse(res);
                if (res.state === 'success') {
                    completeAuthentication(res.data);
                } else {
                    deleteSession();
                    setUser(null);
                    updateIsAuthenticated(false);
                    setIsProfileComplete(false);
                    navigate("/");
                }
                break;
            case 'autologin':
                setResponse(res);
                if (res.state === 'success') {
                    completeAuthentication(res.data, null);
                } else {
                    deleteSession();
                    setUser(null);
                    updateIsAuthenticated(false);
                    setIsProfileComplete(false);
                }
                break;
            case 'logout':
                break;
        }
    }, [responseSuccess]);

    const register = (email, password, name, lastname) => {
        makeRequest('/auth/register', 'post', { email, password, name, lastname, requestType: 'register' });
    }

    const login = (email, password) => {
        makeRequest('/auth/login', 'post', { email, password, requestType: 'login' });
    }

    const logout = () => {
        setUser(null);
        updateIsAuthenticated(false);
        setIsProfileComplete(false);
        deleteSession();
        navigate("/");
    }

    const updateUser = (updatedUserData) => {
        setUser(prev => {
            const nextUser = {
                ...prev,
                ...updatedUserData
            };
            setIsProfileComplete(isPsychologistProfileComplete(nextUser));
            return nextUser;
        });
    }

    const googleAuth = (credentialResponse) => {
        makeRequest('/auth/google', 'post', {
            idToken: credentialResponse.credential,
            requestType: 'login'
        });
    }

    const googleMobileLogin = async () => {
        if (!config.HAS_GOOGLE_OAUTH_CLIENT_ID) {
            setResponse({
                state: 'error',
                error: {
                    message: 'Google login is not configured'
                },
                request: {
                    requestType: 'login'
                }
            });
            return;
        }

        const { result } = await SocialLogin.login({
            provider: 'google',
        });

        makeRequest('/auth/google', 'post', {
            idToken: result.idToken,
            requestType: 'login'
        });
    }

    const requestAdminCode = async () => {
        const result = await makeAdminRequest('/auth/admin/request-code', 'post', {}, { token });

        if (result?.state === 'success' && result?.data?.user) {
            setUser((currentUser) => currentUser ? {
                ...currentUser,
                ...result.data.user,
                adminSessionActive: false
            } : currentUser);
        }

        return result;
    }

    const verifyAdminCode = async (code) => {
        const result = await makeAdminRequest('/auth/admin/verify-code', 'post', { code }, { token });

        if (result?.state === 'success') {
            await completeAuthentication(result.data, null);
        }

        return result;
    }

    return (
        <AuthContext.Provider value={{ user, token, response, isLoading, isAuthenticated, isProfileComplete, register, login, logout, updateUser, googleAuth, googleMobileLogin, completeAuthentication, requestAdminCode, verifyAdminCode }}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext);
